<template>
  <div>
    <NewFranchiseManualSidebar />
    <PublishManualVersionSidebar />

    <!-- Filters -->
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5 class="mb-1">Filtros</h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="6" class="mb-1">
            <b-form-input
              v-model="searchQuery"
              placeholder="Nome ou categoria do manual..."
              @keyup.enter="search"
            />
          </b-col>
          <b-col cols="12" md="6" class="mb-md-0 mb-2 text-right">
            <b-button variant="outline-primary" @click.prevent="search" :disabled="loading">
              <b-spinner v-if="loading" small />
              <feather-icon v-else icon="SearchIcon" class="mr-50" />
              <span>{{ loading ? "Aguarde..." : "Pesquisar" }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Table -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(currentPage)"
            >
              <template #no-options>Sem resultados</template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
          <b-col v-if="createEnabled" cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-button variant="primary" @click="openNewManual(undefined)">
                <feather-icon icon="PlusCircleIcon" class="mr-50" />
                <span class="text-nowrap">Novo Manual</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        hover
        class="position-relative"
        :items="items"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="Nenhum manual do franqueado cadastrado"
        :busy="loading"
      >
        <template #cell(name)="data">
          <div class="text-wrap">
            <span class="align-text-top font-weight-bold">{{ data.item.name }}</span>
          </div>
        </template>

        <template #cell(category)="data">
          <div class="text-wrap">{{ data.item.category }}</div>
        </template>

        <template #cell(contract_types)="data">
          <b-badge
            v-for="contractType in data.item.contract_types"
            :key="contractType.id"
            variant="light-primary"
            class="mr-25 mb-25"
          >
            {{ contractTypeLabel(contractType.user_contract_type_id) }}
          </b-badge>
        </template>

        <template #cell(current_version)="data">
          <span v-if="data.item.current_version">v{{ data.item.current_version.version }}</span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(is_mandatory)="data">
          <b-badge :variant="data.item.is_mandatory ? 'danger' : 'secondary'">
            {{ data.item.is_mandatory ? 'Obrigatório' : 'Opcional' }}
          </b-badge>
        </template>

        <template #cell(active)="data">
          <b-form-checkbox
            :checked="!!data.item.is_active"
            switch
            @input="toggleStatus(data.item.id)"
            :disabled="loading || !updateEnabled"
          />
        </template>

        <template #cell(actions)="data">
          <b-dropdown v-if="updateEnabled" variant="link" no-caret right>
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
            </template>
            <b-dropdown-item @click="openNewManual(data.item.id)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Editar</span>
            </b-dropdown-item>
            <b-dropdown-item @click="openPublishVersion(data.item)">
              <feather-icon icon="UploadIcon" />
              <span class="align-middle ml-50">Publicar nova versão</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">Exibindo {{ pageItemsFrom }} à {{ pageItemsTo }} de {{ totalItems }} itens</span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BFormInput, BButton, BTable, BBadge,
  BDropdown, BDropdownItem, BPagination, BSpinner, BFormCheckbox,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { CREATE_ACTION, UPDATE_ACTION, FRANCHISE_MANUAL } from "@/constants/resources";
import * as sharedTypes from "@/modules/shared/store/types";
import * as types from "../store/types";
import NewFranchiseManualSidebar from "@/modules/franchise_manual/components/NewFranchiseManualSidebar.vue";
import PublishManualVersionSidebar from "@/modules/franchise_manual/components/PublishManualVersionSidebar.vue";

export default {
  components: {
    BCard, BCardHeader, BCardBody, BRow, BCol, BFormInput, BButton, BTable, BBadge,
    BDropdown, BDropdownItem, BPagination, BSpinner, BFormCheckbox,
    vSelect,
    NewFranchiseManualSidebar,
    PublishManualVersionSidebar,
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      loading: false,
      contractTypeOptions: [],
      tableColumns: [
        { key: "name", label: "Nome" },
        { key: "category", label: "Categoria" },
        { key: "contract_types", label: "Tipos de franquia" },
        { key: "current_version", label: "Versão vigente" },
        { key: "is_mandatory", label: "Obrigatoriedade" },
        { key: "active", label: "Ativo" },
        { key: "actions", label: "Ações" },
      ],
      currentPage: 1,
      itemsPerPage: 1,
      searchQuery: "",
    };
  },
  computed: {
    ...mapGetters({
      searchResult: types.FRANCHISE_MANUALS_SEARCH_RESULT,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    createEnabled: function () {
      return this.$can(CREATE_ACTION, FRANCHISE_MANUAL);
    },
    updateEnabled: function () {
      return this.$can(UPDATE_ACTION, FRANCHISE_MANUAL);
    },
    items: function () {
      return this.searchResult?.data || [];
    },
    totalItems: function () {
      return this.searchResult?.total || 0;
    },
    pageItemsFrom: function () {
      return this.searchResult?.from || 0;
    },
    pageItemsTo: function () {
      return this.searchResult?.to || 0;
    },
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage;
    this.loadContractTypeOptions();
    this.search();
  },
  methods: {
    ...mapActions({
      searchFranchiseManuals: types.SEARCH_FRANCHISE_MANUALS,
      openNewFranchiseManualSidebar: types.OPEN_NEW_FRANCHISE_MANUAL_SIDEBAR,
      openPublishManualVersionSidebar: types.OPEN_PUBLISH_MANUAL_VERSION_SIDEBAR,
      toggleFranchiseManualStatus: types.TOGGLE_FRANCHISE_MANUAL_STATUS,
      getContractTypeOptions: types.GET_FRANCHISE_CONTRACT_TYPE_OPTIONS,
    }),
    search(currentPage) {
      this.loading = true;
      this.searchFranchiseManuals({
        searchQuery: this.searchQuery,
        limit: this.itemsPerPage,
        currentPage: Number.isInteger(currentPage) ? currentPage : this.currentPage,
      })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao buscar os manuais. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openNewManual(id) {
      this.openNewFranchiseManualSidebar({
        id,
        saveAction: () => {
          this.search();
        },
      });
    },
    openPublishVersion(manual) {
      this.openPublishManualVersionSidebar({
        id: manual.id,
        manualName: manual.name,
        saveAction: () => {
          this.search();
        },
      });
    },
    toggleStatus(id) {
      this.toggleFranchiseManualStatus(id).catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao alterar o status do manual. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
    },
    loadContractTypeOptions() {
      this.getContractTypeOptions()
        .then((response) => {
          this.contractTypeOptions = response.data || [];
        })
        .catch(() => {});
    },
    contractTypeLabel(id) {
      return this.contractTypeOptions.find((option) => option.id === id)?.name || id;
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
